import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { CommunicationRecord } from "@entities";
import { linko } from "@shared/linko";
import { WEME_APP_BASE_URL } from "@shared/helpers/constants";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { type FC, useEffect, useMemo, useState } from "react";
import { useAlert } from "../../../contexts/AlertProvider";
import { CommunicationRecordsDialog } from "../../../components/CommunicationRecordManager/CommunicationRecordsDialog";
import { TableToolbarTabs } from "../table-tabs/TableToolbarTabs";
import { type Tabs } from "../users-table/UsersTables";
import {
  exportLeadsToCsv,
  LEADS_PAGE_SIZE,
  type SummerCampaignLeadRow,
} from "./helpers";
import { LeadTableRow } from "./LeadTableRow";
import { LeadsStatsBar, type LeadsTab } from "./LeadsStatsBar";
import { LeadsTableToolbar } from "./LeadsTableToolbar";
import { ProcessLeadDialog } from "./ProcessLeadDialog";
import {
  invalidateLeadHandlingQueries,
  patchLeadAfterDecline,
  patchLeadFieldsInList,
  patchLeadHandlingInList,
  refreshLeadHandlingBadges,
} from "../../../queries/invalidate-lead-handling";
import { useUser } from "../../../contexts/UserProvider";
import { LeadActionTooltip } from "./LeadActionTooltip";
import { LEAD_ACTION_TOOLTIPS } from "./lead-actions-copy";
import "./summer-campaign-leads-table.scss";

type LeadRow = SummerCampaignLeadRow;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

const EMPTY_COUNTS = {
  pending: 0,
  mine: 0,
  processed: 0,
  declined: 0,
  all: 0,
  today: 0,
};

const SummerCampaignLeadsTable: FC<Tabs> = (props) => {
  const { activeTab = null, setActiveTab = () => {}, options = [] } = props;
  const queryClient = useQueryClient();
  const { currentUser } = useUser();
  const { setAlertText, setSeverity } = useAlert();
  const [tab, setTab] = useState<LeadsTab>("pending");
  const [phoneSearch, setPhoneSearch] = useState("");
  const debouncedPhoneSearch = useDebouncedValue(phoneSearch, 350);
  const [regionFilter, setRegionFilter] = useState("");
  const [attendanceFilter, setAttendanceFilter] = useState("");
  const [contactStatusFilter, setContactStatusFilter] = useState("");
  const [processLead, setProcessLead] = useState<LeadRow | null>(null);
  const [commAssociationId, setCommAssociationId] = useState<string | null>(null);
  const [commLeadId, setCommLeadId] = useState<string | null>(null);
  const [commTitle, setCommTitle] = useState("");
  const [commLoadingLeadId, setCommLoadingLeadId] = useState<string | null>(null);
  const [registerLoadingLeadId, setRegisterLoadingLeadId] = useState<string | null>(null);
  const [commError, setCommError] = useState<string | null>(null);

  const sharedFilters = {
    ...(debouncedPhoneSearch.trim()
      ? { phoneSearch: debouncedPhoneSearch.trim() }
      : {}),
    ...(contactStatusFilter ? { contactStatus: contactStatusFilter } : {}),
    ...(regionFilter ? { region: regionFilter } : {}),
    ...(attendanceFilter ? { attendance: attendanceFilter } : {}),
  };

  const {
    data: leadsPages,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["summer-campaign-leads", tab, sharedFilters],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const { data } = await linko.summerCampaignLead.queries.listLeads({
        tab,
        ...sharedFilters,
        limit: LEADS_PAGE_SIZE,
        offset: pageParam,
      });
      if (Array.isArray(data)) {
        return {
          items: data,
          hasMore: false,
          limit: data.length,
          offset: pageParam,
        };
      }
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.offset + lastPage.limit : undefined,
    staleTime: 30_000,
  });

  const leads = useMemo(
    () => leadsPages?.pages.flatMap((page) => page.items) ?? [],
    [leadsPages],
  );

  const { data: counts = EMPTY_COUNTS } = useQuery({
    queryKey: ["summer-campaign-leads-stats", sharedFilters],
    queryFn: async () => {
      const { data } = await linko.summerCampaignLead.queries.getLeadStats(
        sharedFilters,
      );
      return data;
    },
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });

  const declineMutation = useMutation({
    mutationFn: async (leadId: string) => {
      await linko.summerCampaignLead.mutations.declineLead({ leadId });
      return leadId;
    },
    onSuccess: (leadId) => {
      patchLeadAfterDecline(queryClient, leadId);
    },
    onError: (err: Error) => {
      setCommError(err.message || "לא ניתן לסמן ליד כלא לטפל");
    },
  });

  const hardRefresh = () => {
    void invalidateLeadHandlingQueries(queryClient);
  };

  const applyCommunicationRecords = (
    leadId: string,
    records?: CommunicationRecord[],
  ) => {
    if (!records) return;
    patchLeadFieldsInList(queryClient, leadId, {
      communicationRecords: records.slice(0, 1),
    });
  };

  const openCommunication = async (lead: LeadRow) => {
    setCommError(null);
    setCommLoadingLeadId(lead.id);
    try {
      const { data } = await linko.summerCampaignLead.mutations.prepareLeadCommunication({
        leadId: lead.id,
      });
      setCommAssociationId(data.associationId ?? null);
      setCommLeadId(data.leadId);
      setCommTitle(lead.phoneNumber);
      if (data.associationId) {
        patchLeadFieldsInList(queryClient, lead.id, {
          associationId: data.associationId,
        });
      }
    } catch (err) {
      setCommError(err instanceof Error ? err.message : "לא ניתן לפתוח תיעוד שיחה");
    } finally {
      setCommLoadingLeadId(null);
    }
  };

  const handleTreatAndRegister = async (lead: LeadRow) => {
    const alreadyMine =
      !!lead.handlingManagerId && lead.handlingManagerId === currentUser?.id;

    if (alreadyMine) {
      setProcessLead(lead);
      return;
    }

    setRegisterLoadingLeadId(lead.id);
    try {
      const { data } = await linko.summerCampaignLead.mutations.claimLeadHandling({
        leadId: lead.id,
      });
      if (!data.success) {
        setSeverity("warning");
        setAlertText(data.message);
        if (data.handlingManagerId) {
          patchLeadHandlingInList(queryClient, {
            leadId: lead.id,
            organizationId: "",
            handlingManagerId: data.handlingManagerId,
            handlingManagerName: data.handlingManagerName ?? null,
            handlingSince: data.handlingSince ?? null,
          });
          refreshLeadHandlingBadges(queryClient);
        }
        return;
      }
      patchLeadHandlingInList(queryClient, {
        leadId: lead.id,
        organizationId: "",
        handlingManagerId: data.handlingManagerId,
        handlingManagerName: data.handlingManagerName,
        handlingSince: data.handlingSince,
      });
      refreshLeadHandlingBadges(queryClient);
      setProcessLead({
        ...lead,
        handlingManagerId: data.handlingManagerId,
        handlingManagerName: data.handlingManagerName,
        handlingSince: data.handlingSince
          ? new Date(data.handlingSince)
          : null,
      });
    } catch {
      setSeverity("error");
      setAlertText("לא ניתן לסמן את הליד בטיפול");
    } finally {
      setRegisterLoadingLeadId(null);
    }
  };

  return (
    <Stack
      gap={2}
      p={3}
      className="leads-table"
      sx={{
        width: "100%",
        flex: 1,
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <TableToolbarTabs
          value={activeTab}
          onChange={(tab) => setActiveTab(tab)}
          options={options}
        />
        <Stack direction="row" gap={1} alignItems="center">
          {isFetching && !isLoading ? (
            <CircularProgress size={18} />
          ) : null}
          <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.copyLoginLink}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                void navigator.clipboard.writeText(WEME_APP_BASE_URL);
                setAlertText("קישור הכניסה הועתק ללוח");
                setSeverity("success");
              }}
            >
              קישור הכניסה
            </Button>
          </LeadActionTooltip>
          <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.exportCsv}>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                exportLeadsToCsv(leads as Array<Record<string, unknown>>)
              }
              disabled={leads.length === 0}
            >
              ייצוא Excel
            </Button>
          </LeadActionTooltip>
          <LeadActionTooltip title="רענון מלא של הרשימה">
            <Button variant="text" size="small" onClick={hardRefresh}>
              רענון
            </Button>
          </LeadActionTooltip>
        </Stack>
      </Stack>

      <LeadsStatsBar counts={counts} activeTab={tab} onTabSelect={setTab} />

      <LeadsTableToolbar
        phoneSearch={phoneSearch}
        onPhoneSearchChange={setPhoneSearch}
        regionFilter={regionFilter}
        onRegionFilterChange={setRegionFilter}
        attendanceFilter={attendanceFilter}
        onAttendanceFilterChange={setAttendanceFilter}
        contactStatusFilter={contactStatusFilter}
        onContactStatusFilterChange={setContactStatusFilter}
        resultCount={leads.length}
      />

      {commError && (
        <Typography color="error" variant="body2">
          {commError}
        </Typography>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto", width: "100%" }}>
          <Table size="small" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell>ליד</TableCell>
                <TableCell>העדפות</TableCell>
                <TableCell>סטטוס</TableCell>
                <TableCell width={180}>תיעוד תקשורת</TableCell>
                <TableCell width={160}>טיפול</TableCell>
                <TableCell width={200}>פעולות</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.map((lead) => (
                <LeadTableRow
                  key={lead.id}
                  lead={lead}
                  onTreatAndRegister={handleTreatAndRegister}
                  onCommunication={(l) => void openCommunication(l)}
                  onDecline={(l) => {
                    if (window.confirm("לסמן ליד זה כלא לטפל?")) {
                      declineMutation.mutate(l.id);
                    }
                  }}
                  onCommunicationSaved={(records) =>
                    applyCommunicationRecords(lead.id, records)
                  }
                  onCallInitiated={() => {
                    // Voicenter may create a record shortly after dial - light badge refresh.
                    window.setTimeout(
                      () => refreshLeadHandlingBadges(queryClient),
                      5000,
                    );
                  }}
                  commLoading={commLoadingLeadId === lead.id}
                  registerLoading={registerLoadingLeadId === lead.id}
                />
              ))}
              {leads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary" py={2} textAlign="center">
                      אין לידים להצגה
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {hasNextPage ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={isFetchingNextPage}
                      onClick={() => void fetchNextPage()}
                      sx={{ my: 1 }}
                    >
                      {isFetchingNextPage ? "טוען..." : "טען עוד לידים"}
                    </Button>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {processLead && (
        <ProcessLeadDialog
          lead={processLead}
          open={!!processLead}
          onClose={() => setProcessLead(null)}
          onDone={() => {
            // List already patched on process success.
            setProcessLead(null);
          }}
        />
      )}

      <CommunicationRecordsDialog
        open={!!commLeadId}
        onClose={() => {
          setCommAssociationId(null);
          setCommLeadId(null);
          setCommError(null);
        }}
        title={commTitle}
        associationId={commAssociationId ?? undefined}
        summerCampaignLeadId={commLeadId ?? undefined}
        type="lead"
        onSave={(records) => {
          if (commLeadId) applyCommunicationRecords(commLeadId, records);
        }}
      />
    </Stack>
  );
};

export default SummerCampaignLeadsTable;
